import React, { Dispatch, useEffect, useMemo, useState } from 'react';

import alertErrorTokens from '@amzn/meridian-tokens/base/icon/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { CellClassRules, GridApi } from 'ag-grid-community';
import { cloneDeep, isEmpty } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { showPlayCustomerProgramTypeCols } from 'src/components/ViewPrimeAMFRateCard/ViewCoreRates/cppMauColDefs';
import { MISSING_REQUIRED_VALUES } from 'src/constants/errorMessages';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues, IRateCardGridRowData } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors, generateGridErrorId } from '../../rateCardValidationReducer';
import { getCppMauColDefs } from '../cppMauColDefs';

interface IAddMauGridProps {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    filterText: string;
    setMauGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
    augmentedMetadata: IMetadata;
}

export const AddMauGrid = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    filterText,
    setMauGridApi,
    augmentedMetadata
}: IAddMauGridProps) => {
    const [internalMauRates, setInternalMauRates] = useState<IRateCardGridRowData[]>(
        cloneDeep(rateCardValues.rateCardRowData.mau)
    );
    const services = augmentedMetadata.serviceMapping;
    const isPrimeRateCard = rateCardValues.serviceId === services[2].serviceId;
    const CAEs = Object.values(augmentedMetadata.CaeMapping!);
    const launchedTerritoryIdList = useMemo(
        () =>
            augmentedMetadata
                .activeServiceLaunchDateMapping![
                    rateCardValues.serviceId
                ].filter((territory) => territory.territoryCode === 'IN')
                .map((territory) => territory.territoryID),
        [augmentedMetadata.activeServiceLaunchDateMapping]
    );

    const cellClassRules = useMemo<CellClassRules>(
        () => ({
            'error-cell': (params) =>
                rateCardErrors.rateCardRowData.mau.has(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    generateGridErrorId(Number(params.node.id), params.colDef.field!)
                )
        }),
        [rateCardErrors.rateCardRowData.mau]
    );
    const colDefs = getCppMauColDefs(augmentedMetadata).map((colDef) => ({ ...colDef, cellClassRules }));

    useEffect(() => {
        let newMauRateRowData = launchedTerritoryIdList.map((territoryId) => ({
            territoryId: territoryId!,
            rateCardCurrency: augmentedMetadata.territoryMapping[territoryId!].currency ?? '',
            settlementCurrency: augmentedMetadata.territoryMapping[territoryId!].settlementCurrency ?? '',
            cae:
                CAEs.find((CAE) => CAE.territoryID === territoryId && CAE.serviceID === rateCardValues.serviceId)
                    ?.contentAcquisitionEntityCode ?? '',
            playType: '',
            programType: '',
            rate: ''
        })) as IRateCardGridRowData[];

        const customerTypes =
            (isPrimeRateCard ? rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType : []) ?? [];

        if (!isEmpty(customerTypes)) {
            newMauRateRowData = customerTypes.flatMap((customerType) =>
                newMauRateRowData.map((row) => ({
                    ...row,
                    paperPlanName: customerType
                }))
            );
        }

        // maintain previous user input on rate values in the grid
        newMauRateRowData = newMauRateRowData.map((newRow) => {
            const matchedRow = internalMauRates.find(
                (prevRow) =>
                    prevRow.territoryId === newRow.territoryId && prevRow.paperPlanName === newRow.paperPlanName
            );
            return matchedRow ?? newRow;
        });

        setInternalMauRates(newMauRateRowData);
    }, []);

    // Sync internalMauRates with parent rateCardValues and validate
    useEffect(() => {
        syncMauRatesState();
    }, [internalMauRates]);

    const onCellValueChanged = () => {
        syncMauRatesState();
    };

    const syncMauRatesState = () => {
        dispatchRateCardValues({
            type: 'set_mau_grid_row_data',
            rowData: internalMauRates
        });
    };

    // Validate
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_mau_rates',
            rateCardValues,
            visibleColumns: colDefs.filter((col) => !col.hide)
        });
    }, [rateCardValues.rateCardRowData]);

    showPlayCustomerProgramTypeCols(internalMauRates, colDefs, true);

    return (
        <Column heights={['fit', 'fill']}>
            <Row>
                <Heading level={3}>Monthly active user</Heading>
                {!isEmpty(rateCardErrors.rateCardRowData.mau) && (
                    <Tooltip position={'top'} title={MISSING_REQUIRED_VALUES}>
                        <Box type="fill" spacingInset="200 300">
                            <Row spacing="300">
                                <Icon tokens={alertErrorTokens} />
                                <Text type="h100" data-testid="error-row-count">
                                    {rateCardErrors.rateCardRowData.mau.size}
                                </Text>
                                <Text>invalid</Text>
                            </Row>
                        </Box>
                    </Tooltip>
                )}
            </Row>
            <Column minHeight={300}>
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={internalMauRates}
                    onCellValueChanged={onCellValueChanged}
                    suppressRowClickSelection={true}
                    suppressSizeColumnsToFit={true}
                    singleClickEdit={false}
                    filterText={filterText}
                    setAgGridApi={setMauGridApi}
                />
            </Column>
        </Column>
    );
};
